import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, HelpCircle, ArrowRight, ChevronRight, ChevronDown,
  BookOpen, Users, Beaker, MessageSquare, CreditCard, Shield,
  Settings, Globe, Zap, Mail, Clock, FileText, AlertCircle,
  CheckCircle2, LifeBuoy, Headphones
} from 'lucide-react';
import landingService from '../../../services/landingService';
import ResourcesLayout from './ResourcesLayout';
import './HelpCenterPage.css';

const HelpCenterPage = () => {

  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [faqs, setFaqs] = useState([]);
  const [statusItems, setStatusItems] = useState([]);
  const [overallStatus, setOverallStatus] = useState('operational');
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Fetch FAQs and system status from API
  useEffect(() => {
    const fetchData = async () => {
      const [faqRes, statusRes] = await Promise.all([
        landingService.getFaqs(),
        landingService.getSystemStatus(),
      ]);
      if (faqRes.success) setFaqs(faqRes.faqs || []);
      if (statusRes.success) {
        setStatusItems(statusRes.services || []);
        setOverallStatus(statusRes.overallStatus || 'operational');
      }
    };
    fetchData();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const helpCategories = [
    {
      icon: BookOpen,
      title: 'Getting Started',
      description: 'Account setup, onboarding, and first steps',
      articles: 15,
      color: '#00C853',
    },
    {
      icon: Search,
      title: 'Paper Search & Library',
      description: 'Searching, saving, citing, and bookmarks',
      articles: 12,
      color: '#2979FF',
    },
    {
      icon: Beaker,
      title: 'Research Labs',
      description: 'Labs, members, chat, tasks, files, calendar',
      articles: 22,
      color: '#7C4DFF',
    },
    {
      icon: Users,
      title: 'Communities',
      description: 'Threads, moderation, and analytics',
      articles: 18,
      color: '#FF8F00',
    },
    {
      icon: MessageSquare,
      title: 'Messaging',
      description: 'DMs, encryption, reactions, file sharing',
      articles: 10,
      color: '#00BCD4',
    },
    {
      icon: Shield,
      title: 'Privacy & Security',
      description: 'E2EE, account security, data privacy, 2FA',
      articles: 8,
      color: '#FF5252',
    },
    {
      icon: CreditCard,
      title: 'Billing & Plans',
      description: 'Subscriptions, invoices, and upgrades',
      articles: 9,
      color: '#4CAF50',
    },
    {
      icon: Settings,
      title: 'Account & Settings',
      description: 'Profile, notifications, and preferences',
      articles: 14,
      color: '#607D8B',
    },
  ];

  const contactOptions = [
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Response within 24 hours',
      action: 'mailto:support@ResearchPro.io',
      actionLabel: 'Send Email',
      external: true,
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      description: 'Mon–Fri, 9am–6pm UTC',
      action: '/contact',
      actionLabel: 'Start Chat',
    },
    {
      icon: Users,
      title: 'Community Forum',
      description: 'Ask the community',
      action: '/communities',
      actionLabel: 'Visit Forum',
    },
  ];

  const statusLabel = overallStatus === 'operational' ? 'All Systems Operational'
    : overallStatus === 'degraded' ? 'Partial Degradation'
    : 'Service Disruption';

  return (
    <ResourcesLayout>
      {/* ── Hero ── */}
      <section className="help-hero">
        <div className="help-hero-inner">
          <h1 className="help-hero-title">
            How can we <span className="help-hero-gradient">help you?</span>
          </h1>
          <p className="help-hero-subtitle">
            Search our knowledge base, browse categories, or reach out 
            to our support team directly.
          </p>

        </div>
      </section>

      {/* ── Help Categories ── */}
      <section
        id="help-categories"
        className={`help-section ${isVisible('help-categories') ? 'help-visible' : ''}`}
        ref={(el) => (sectionRefs.current['help-categories'] = el)}
      >
        <div className="help-section-inner">
          <div className="help-section-header">
            <span className="help-section-number">01</span>
            <div>
              <h2 className="help-section-title">Browse by Category</h2>
              <p className="help-section-subtitle">Find answers organized by topic</p>
            </div>
          </div>

          <div className="help-categories-grid">
            {helpCategories.map((cat, i) => (
              <Link key={i} to="#" className="help-cat-card" style={{ '--cat-color': cat.color, '--card-delay': `${i * 50}ms` }}>
                <div className="help-cat-icon">
                  <cat.icon size={20} />
                </div>
                <div className="help-cat-body">
                  <h3 className="help-cat-title">{cat.title}</h3>
                  <p className="help-cat-desc">{cat.description}</p>
                </div>
                <div className="help-cat-count">
                  <span>{cat.articles}</span>
                  <ChevronRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section
        id="help-faq"
        className={`help-section help-section--alt ${isVisible('help-faq') ? 'help-visible' : ''}`}
        ref={(el) => (sectionRefs.current['help-faq'] = el)}
      >
        <div className="help-section-inner">
          <div className="help-section-header">
            <span className="help-section-number">02</span>
            <div>
              <h2 className="help-section-title">Frequently Asked Questions</h2>
              <p className="help-section-subtitle">Quick answers to common questions</p>
            </div>
          </div>

          <div className="help-faq-list">
            {faqs.map((faq, i) => (
              <div
                key={faq._id || i}
                className={`help-faq-item ${expandedFAQ === i ? 'help-faq-item--open' : ''}`}
              >
                <button
                  className="help-faq-question"
                  onClick={() => setExpandedFAQ(expandedFAQ === i ? null : i)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={16} className="help-faq-chevron" />
                </button>
                <div className="help-faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section
        id="help-contact"
        className={`help-section ${isVisible('help-contact') ? 'help-visible' : ''}`}
        ref={(el) => (sectionRefs.current['help-contact'] = el)}
      >
        <div className="help-section-inner">
          <div className="help-section-header">
            <span className="help-section-number">03</span>
            <div>
              <h2 className="help-section-title">Still need help?</h2>
              <p className="help-section-subtitle">Reach out through any of these channels</p>
            </div>
          </div>

          <div className="help-contact-grid">
            {contactOptions.map((opt, i) => {
              const isExternal = opt.external || opt.action.startsWith('mailto:') || opt.action.startsWith('http');
              const Wrapper = isExternal ? 'a' : Link;
              const wrapperProps = isExternal
                ? { href: opt.action, target: opt.action.startsWith('http') ? '_blank' : undefined, rel: opt.action.startsWith('http') ? 'noopener noreferrer' : undefined }
                : { to: opt.action };
              return (
                <Wrapper key={i} {...wrapperProps} className="help-contact-card">
                  <div className="help-contact-icon">
                    <opt.icon size={20} />
                  </div>
                  <h3 className="help-contact-title">{opt.title}</h3>
                  <p className="help-contact-desc">{opt.description}</p>
                  <span className="help-contact-link">
                    {opt.actionLabel} <ArrowRight size={13} />
                  </span>
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── System Status ── */}
      <section className="help-status">
        <div className="help-status-inner">
          <div className="help-status-header">
            <CheckCircle2 size={16} className="help-status-check" />
            <span className="help-status-title">{statusLabel}</span>
          </div>
          <div className="help-status-items">
            {statusItems.map((item, i) => (
              <div key={item._id || i} className="help-status-item">
                <span className={`help-status-dot ${item.status !== 'operational' ? 'help-status-dot--warn' : ''}`} />
                <span className="help-status-label">{item.name || item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="help-cta">
        <div className="help-cta-inner">
          <span className="help-cta-badge">
            <Zap size={14} />
            Get Started
          </span>
          <h2 className="help-cta-title">Ready to accelerate your research?</h2>
          <p className="help-cta-subtitle">
            Join 50,000+ researchers already using ResearchPro to 
            accelerate their discoveries.
          </p>
          <div className="help-cta-actions">
            <Link to="/signup" className="help-btn help-btn--primary">
              Create Free Account
              <ArrowRight size={16} />
            </Link>
            <Link to="/resources" className="help-btn help-btn--secondary">
              All Resources
            </Link>
          </div>
        </div>
      </section>
    </ResourcesLayout>
  );
};

export default HelpCenterPage;
